/**
 * CustomerStory — Public per-customer "Wall of Fame" detail page.
 *
 * Route: /wall-of-fame/c/:slug  (no auth)
 *
 * Renders one design partner / customer's:
 *   • Hero with logo + company name + industry + total saved $
 *   • Narrative blurb (story_blurb)
 *   • Per-engineer leaderboard (alias-respecting)
 *   • 6-month savings timeline (mini bar chart)
 *   • Share-PNG CTA (deep-links to /api/wall-of-fame/card.png?kind=customer)
 *   • Back to Wall of Fame + "Add your company" CTAs
 *
 * Returns 404 cleanly when the slug doesn't map to an opt-in company so
 * private customers stay private.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useParams } from 'react-router-dom';
import SEOHead from '../components/SEOHead';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import {
  Trophy, Sparkles, Share2, ArrowRight, ArrowLeft, Building2, Users,
  TrendingDown, ExternalLink, Calendar, GitCommit,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtUsd = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `$${(v / 1_000).toFixed(0)}K`;
  return `$${Math.round(v).toLocaleString()}`;
};

const fmtMonth = (yyyyMm) => {
  if (!yyyyMm || yyyyMm.length < 7) return yyyyMm;
  const d = new Date(`${yyyyMm}-01T00:00:00Z`);
  return d.toLocaleString('en-US', { month: 'short', year: '2-digit' });
};

export default function CustomerStory() {
  const { slug } = useParams();
  const [story, setStory]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState(null);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/wall-of-fame/company/${encodeURIComponent(slug)}`)
      .then((r) => setStory(r.data))
      .catch((e) => setError(e?.response?.status === 404 ? 'not_found' : 'unknown'))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 grid place-items-center">
        <div className="text-slate-500 text-sm">Loading customer story…</div>
      </div>
    );
  }

  if (error === 'not_found' || !story) {
    return (
      <div className="min-h-screen bg-slate-950 text-slate-100 grid place-items-center px-6">
        <SEOHead title="Customer story not found · CARTIEAI"
                 description="This customer story is private or has not opted in to the Wall of Fame." />
        <div className="max-w-md text-center">
          <Building2 className="w-10 h-10 text-slate-700 mx-auto mb-4" />
          <h1 className="text-2xl font-medium mb-2">Customer not found</h1>
          <p className="text-sm text-slate-400 mb-6">
            This company is either private or hasn&apos;t opted in to the Wall of Fame yet.
          </p>
          <Link to="/wall-of-fame"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500/15 ring-1 ring-emerald-500/30 text-emerald-300 hover:bg-emerald-500/25 transition text-sm"
                data-testid="cs-back-to-wall">
            <ArrowLeft className="w-4 h-4" /> Back to Wall of Fame
          </Link>
        </div>
      </div>
    );
  }

  // Build share-PNG URL with this customer's totals
  const shareUrl =
    `${process.env.REACT_APP_BACKEND_URL}/api/wall-of-fame/card.png?` +
    `kind=customer` +
    `&label=${encodeURIComponent(story.company_name)}` +
    `&value_usd=${Math.round(Number(story.total_saved_usd || 0))}` +
    `&subtitle=${encodeURIComponent(`${story.engineer_count} engineers · ${story.commits_count} commits`)}`;

  // Find timeline max for mini-bar chart scaling
  const tlMax = Math.max(1, ...story.timeline.map((t) => Number(t.saved_usd || 0)));

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <SEOHead
        title={`${story.company_name} — Verified savings on cloud + AI · CARTIEAI`}
        description={
          story.story_blurb
            ? `${story.company_name} saved ${fmtUsd(story.total_saved_usd)} across ${story.engineer_count} engineers and ${story.commits_count} commits. ${story.story_blurb.slice(0, 140)}…`
            : `${story.company_name} saved ${fmtUsd(story.total_saved_usd)} verified by CARTIEAI Bill Detective.`
        }
        ogImage={shareUrl}
        ogType="article"
      />

      {/* ── Hero ── */}
      <section className="relative overflow-hidden border-b border-slate-900">
        <div className="absolute inset-0 pointer-events-none opacity-30"
             style={{ backgroundImage:
              'radial-gradient(circle at 20% 30%, rgba(16,185,129,0.12) 0%, transparent 50%), ' +
              'radial-gradient(circle at 80% 70%, rgba(16,185,129,0.06) 0%, transparent 50%)',
             }} />
        <div className="max-w-5xl mx-auto px-6 pt-16 pb-12 relative">
          <Link to="/wall-of-fame"
                className="inline-flex items-center text-xs text-slate-400 hover:text-slate-100 mb-6"
                data-testid="cs-breadcrumb-wall">
            <ArrowLeft className="w-3 h-3 mr-1" /> Wall of Fame
          </Link>

          <div className="flex items-center gap-3 mb-6">
            {story.company_logo_url ? (
              <img src={story.company_logo_url} alt={story.company_name}
                   className="h-10 w-auto" data-testid="cs-logo" />
            ) : (
              <div className="w-10 h-10 rounded bg-emerald-500/15 ring-1 ring-emerald-500/30 grid place-items-center">
                <Building2 className="w-5 h-5 text-emerald-300" />
              </div>
            )}
            <div>
              <h1 className="text-3xl sm:text-4xl font-medium tracking-tight" data-testid="cs-company-name">
                {story.company_name}
              </h1>
              {story.industry && (
                <div className="text-xs text-slate-500 mt-1" data-testid="cs-industry">
                  {story.industry}
                </div>
              )}
            </div>
            {story.is_million_club && (
              <span className="ml-auto inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/15 ring-1 ring-emerald-500/30 text-emerald-300 text-xs font-medium"
                    data-testid="cs-million-club">
                <Trophy className="w-3.5 h-3.5" /> $1M+ club
              </span>
            )}
          </div>

          <div className="grid grid-cols-3 gap-4 max-w-xl">
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Saved</div>
              <div className="text-3xl sm:text-4xl font-mono text-emerald-300" data-testid="cs-total-saved">
                {fmtUsd(story.total_saved_usd)}
              </div>
              <div className="text-[11px] text-slate-500 mt-0.5">last {story.lookback_days} days</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Engineers</div>
              <div className="text-3xl sm:text-4xl font-mono text-slate-100" data-testid="cs-engineer-count">
                {story.engineer_count}
              </div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-slate-500 mb-1">Commits</div>
              <div className="text-3xl sm:text-4xl font-mono text-slate-100" data-testid="cs-commits-count">
                {story.commits_count}
              </div>
            </div>
          </div>

          <div className="flex flex-wrap gap-3 mt-8">
            <a href={shareUrl} target="_blank" rel="noopener noreferrer"
               className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 ring-1 ring-slate-800 hover:ring-slate-700 text-sm transition"
               data-testid="cs-share-png">
              <Share2 className="w-4 h-4" /> Share this story (PNG)
            </a>
            {story.canonical_url && (
              <a href={story.canonical_url} target="_blank" rel="noopener noreferrer"
                 className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-slate-900 ring-1 ring-slate-800 hover:ring-slate-700 text-sm transition"
                 data-testid="cs-canonical">
                Visit {story.company_name} <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
            <Link to="/pricing/contract">
              <Button className="bg-emerald-500 hover:bg-emerald-400 text-slate-950"
                      data-testid="cs-cta-add-company">
                Add your company <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Narrative blurb ── */}
      {story.story_blurb && (
        <section className="max-w-3xl mx-auto px-6 py-14">
          <h2 className="text-xs uppercase tracking-wider text-emerald-300 mb-3 inline-flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" /> The story
          </h2>
          <p className="text-lg leading-relaxed text-slate-300" data-testid="cs-story-blurb">
            {story.story_blurb}
          </p>
          <p className="text-xs text-slate-500 mt-6 italic">
            All dollar figures verified by CARTIE&apos;s nightly cloud-bill reconciliation.
            Engineer names shown per {story.company_name}&apos;s chosen privacy format
            ({story.engineer_alias_format.replace('_', ' ')}).
          </p>
        </section>
      )}

      {/* ── Engineer breakdown ── */}
      <section className="max-w-5xl mx-auto px-6 py-12 border-t border-slate-900">
        <h2 className="text-2xl font-medium mb-6">
          <Users className="w-5 h-5 inline mr-2 text-emerald-300" />
          Engineers on the wall
        </h2>
        {story.engineers.length === 0 ? (
          <Card className="bg-slate-900/40 ring-1 ring-slate-800" data-testid="cs-empty-engineers">
            <CardContent className="p-8 text-center text-slate-500">
              No saving commits in the lookback window yet.
            </CardContent>
          </Card>
        ) : (
          <Card className="bg-slate-900/40 ring-1 ring-slate-800" data-testid="cs-engineers-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-slate-400 border-b border-slate-800">
                  <tr>
                    <th className="text-left p-4 font-medium">#</th>
                    <th className="text-left p-4 font-medium">Engineer</th>
                    <th className="text-right p-4 font-medium">Saved /mo</th>
                    <th className="text-right p-4 font-medium">Commits</th>
                  </tr>
                </thead>
                <tbody>
                  {story.engineers.map((e, i) => (
                    <tr key={`${i}-${e.engineer_alias}`}
                        className="border-b border-slate-800/60 hover:bg-slate-800/30"
                        data-testid={`cs-engineer-${i + 1}`}>
                      <td className="p-4 text-slate-500 font-mono">{i + 1}</td>
                      <td className="p-4 text-slate-100">{e.engineer_alias}</td>
                      <td className="p-4 text-right text-emerald-300 font-mono">
                        <TrendingDown className="w-3 h-3 inline mr-1" />
                        {fmtUsd(e.saved_usd_monthly)}
                      </td>
                      <td className="p-4 text-right text-slate-400 font-mono">{e.commits_count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </section>

      {/* ── Timeline mini bar chart ── */}
      {story.timeline.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 py-12 border-t border-slate-900">
          <h2 className="text-2xl font-medium mb-6">
            <Calendar className="w-5 h-5 inline mr-2 text-emerald-300" />
            Monthly savings timeline
          </h2>
          <Card className="bg-slate-900/40 ring-1 ring-slate-800">
            <CardContent className="p-6">
              <div className="grid gap-2" data-testid="cs-timeline">
                {story.timeline.map((t) => {
                  const pct = (Number(t.saved_usd) / tlMax) * 100;
                  return (
                    <div key={t.month} className="grid grid-cols-[80px_1fr_100px] items-center gap-3 text-sm"
                         data-testid={`cs-timeline-${t.month}`}>
                      <div className="text-slate-500 font-mono text-xs">{fmtMonth(t.month)}</div>
                      <div className="h-3 rounded bg-slate-800/80 overflow-hidden">
                        <div className="h-full bg-emerald-500/70 rounded transition-all duration-500"
                             style={{ width: `${pct}%` }} />
                      </div>
                      <div className="text-right text-emerald-300 font-mono text-xs">
                        {fmtUsd(t.saved_usd)} <GitCommit className="w-3 h-3 inline text-slate-500" /> {t.commits}
                      </div>
                    </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        </section>
      )}

      {/* ── Footer CTA ── */}
      <section className="max-w-5xl mx-auto px-6 py-16 border-t border-slate-900 text-center">
        <h3 className="text-2xl font-medium mb-3">
          Could your team be on the wall?
        </h3>
        <p className="text-sm text-slate-400 mb-6 max-w-lg mx-auto">
          We start every contract with a 3× ROI guarantee. If we can&apos;t save you
          at least 3× the price within 90 days, you don&apos;t pay.
        </p>
        <Link to="/pricing/contract">
          <Button size="lg" className="bg-emerald-500 hover:bg-emerald-400 text-slate-950"
                  data-testid="cs-footer-cta-contract">
            See the ROI contract <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
        </Link>
      </section>
    </div>
  );
}
